import type {
  ArtifactRecord,
  ContentBlock,
  ToolCallStatus,
} from '../../../types'
import { RENDER_TOOL_NAMES } from '../../../utils/artifactKinds'
import type { ToolResultStatus } from '../../../utils/extractArtifacts'
import { summarizeArgs, summarizeResult } from '../../../utils/toolSummaries'

export interface ToolCallSummary {
  id: string
  name: string
  status: 'pending' | 'running' | 'ok' | 'error'
  errorCode?: string | null
  // An errored attempt that a later call of the same tool resolved. It renders
  // as a muted "retried" chip instead of a hard failure, so the user sees the
  // retry and then the success rather than a stuck error.
  superseded?: boolean
  startedAt?: number | null
  finishedAt?: number | null
  argsSummary?: string | null
  resultSummary?: string | null
  // Raw payloads surfaced into the chip's expanded view. `args` is the
  // model's tool_use input; `result` is the persisted tool_result payload.
  // Both are `unknown` because shapes vary per tool — the expand renderer
  // pretty-prints whatever JSON it gets.
  args?: unknown
  result?: unknown
  stepIndex?: number
}

export type RenderItem =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'artifact'; key: string; artifact: ArtifactRecord }
  | {
      kind: 'tool-group'
      key: string
      calls: ToolCallSummary[]
      stepIndex?: number
    }
  | { kind: 'step-end'; key: string; stepIndex: number }

interface ToolUseBlock {
  type: 'tool_use'
  id: string
  name: string
  input: unknown
}

export function isRenderTool(name: string) {
  return RENDER_TOOL_NAMES.has(name)
}

function statusFor(
  toolUseId: string,
  toolCallStatuses?: Record<string, ToolCallStatus>,
  toolResults?: Record<string, ToolResultStatus>,
): {
  status: 'pending' | 'running' | 'ok' | 'error'
  errorCode?: string | null
} {
  const live = toolCallStatuses?.[toolUseId]
  if (live) {
    if (live.ok === null) {
      // tool_plan announces the call with startedAt = null; tool_call_started
      // sets it. The pending visual lets the upfront checklist read distinctly
      // from a call that's actually executing.
      return { status: live.startedAt == null ? 'pending' : 'running' }
    }
    return live.ok
      ? { status: 'ok' }
      : { status: 'error', errorCode: live.errorCode }
  }

  const persisted = toolResults?.[toolUseId]
  if (persisted) {
    return persisted.isError
      ? { status: 'error', errorCode: persisted.errorCode ?? null }
      : { status: 'ok' }
  }

  return { status: 'ok' }
}

// A stable signature of a tool call's arguments, so a later success can be
// recognized as a retry of the SAME operation rather than just the same tool.
// A false negative (e.g. the model reorders keys) only fails safe: the error
// stays a real failure instead of being hidden as a retry.
function argsSignature(input: unknown): string {
  return JSON.stringify(input ?? null)
}

// An errored tool call that a LATER call of the same tool WITH THE SAME
// arguments resolved successfully is a transient failure the model recovered
// from (e.g. the data service hiccuped, the model called the tool again and it
// worked). Mark those errors so the chip renders as a muted "retried" attempt
// rather than a stuck red failure. Matching on arguments, not just the tool
// name, so an unrelated later call of the same tool (a different account or
// filter) does not hide a genuinely failed query. A render tool is the
// exception: the model fixes its bad arguments and draws again, so any later
// success of that tool is the retry. Scanning the whole turn's calls (not just
// one step's group) because the retry lands in a later step.
function computeSupersededErrorIds(
  blocks: ContentBlock[],
  toolCallStatuses?: Record<string, ToolCallStatus>,
  toolResults?: Record<string, ToolResultStatus>,
): Set<string> {
  const calls = blocks
    .filter((block): block is ToolUseBlock => block.type === 'tool_use')
    .map((block) => ({
      id: block.id,
      name: block.name,
      args: argsSignature(block.input),
      status: statusFor(block.id, toolCallStatuses, toolResults).status,
    }))
  const superseded = new Set<string>()
  calls.forEach((call, index) => {
    if (call.status !== 'error') return
    const recoveredLater = calls
      .slice(index + 1)
      .some(
        (later) =>
          later.name === call.name &&
          (isRenderTool(call.name) || later.args === call.args) &&
          later.status === 'ok',
      )
    if (recoveredLater) superseded.add(call.id)
  })
  return superseded
}

function buildToolCallSummary(
  block: ToolUseBlock,
  toolCallStatuses?: Record<string, ToolCallStatus>,
  toolResults?: Record<string, ToolResultStatus>,
  superseded = false,
): ToolCallSummary {
  const { status, errorCode } = statusFor(
    block.id,
    toolCallStatuses,
    toolResults,
  )
  const live = toolCallStatuses?.[block.id]
  const persisted = toolResults?.[block.id]
  return {
    id: block.id,
    name: block.name,
    status,
    errorCode,
    superseded,
    startedAt: live?.startedAt ?? null,
    finishedAt: live?.finishedAt ?? null,
    argsSummary: summarizeArgs(block.name, block.input),
    resultSummary:
      status === 'ok' && persisted?.payload !== undefined
        ? summarizeResult(block.name, persisted.payload)
        : null,
    args: block.input,
    result: persisted?.payload,
    stepIndex: live?.stepIndex,
  }
}

function appendToolCall(
  items: RenderItem[],
  block: ToolUseBlock,
  call: ToolCallSummary,
) {
  const previous = items[items.length - 1]
  // Keep consecutive tool calls in one visual group, but break the group at
  // step boundaries so the per-step usage chip lands under its own cluster.
  if (
    previous &&
    previous.kind === 'tool-group' &&
    previous.stepIndex === call.stepIndex
  ) {
    previous.calls.push(call)
    return
  }
  items.push({
    kind: 'tool-group',
    key: `tools-${block.id}`,
    calls: [call],
    stepIndex: call.stepIndex,
  })
}

function pushStepEnd(items: RenderItem[], endedStep: number): void {
  items.push({
    kind: 'step-end',
    key: `step-end-${endedStep}`,
    stepIndex: endedStep,
  })
}

// Tool-groups are split at model-step boundaries so each step's UsageChip
// can render under its own cluster. When diagnostics are off those chips
// aren't shown, leaving the splits with nothing to fill them — consecutive
// tool chips then alternate between tight (intra-step) and loose (inter-step)
// spacing for no visible reason. This drops the step-end markers and fuses
// adjacent tool-groups so every back-to-back tool chip shares one uniform
// gap. Text and artifact items still separate groups, as before.
export function collapseToolGroups(items: RenderItem[]): RenderItem[] {
  const collapsed: RenderItem[] = []
  for (const item of items) {
    if (item.kind === 'step-end') continue
    const previous = collapsed[collapsed.length - 1]
    if (item.kind === 'tool-group' && previous?.kind === 'tool-group') {
      collapsed[collapsed.length - 1] = {
        ...previous,
        calls: [...previous.calls, ...item.calls],
      }
      continue
    }
    collapsed.push(item)
  }
  return collapsed
}

export function buildRenderItems(
  blocks: ContentBlock[],
  toolCallStatuses?: Record<string, ToolCallStatus>,
  artifacts?: Record<string, ArtifactRecord>,
  toolResults?: Record<string, ToolResultStatus>,
  stepBoundaries?: number[],
): RenderItem[] {
  const items: RenderItem[] = []
  // stepBoundaries[N] is the first block index for step N. We insert a
  // step-end marker into items just before crossing each boundary >= 1, so
  // step N-1's usage chip can render under step N-1's content.
  const boundaries = stepBoundaries ?? []
  let nextBoundaryIdx = 1
  const supersededErrorIds = computeSupersededErrorIds(
    blocks,
    toolCallStatuses,
    toolResults,
  )

  function flushBoundariesBefore(blockIndex: number) {
    while (
      nextBoundaryIdx < boundaries.length &&
      boundaries[nextBoundaryIdx] <= blockIndex
    ) {
      pushStepEnd(items, nextBoundaryIdx - 1)
      nextBoundaryIdx += 1
    }
  }

  blocks.forEach((block, index) => {
    flushBoundariesBefore(index)

    if (block.type === 'text') {
      if (!block.text || block.text.trim().length === 0) return
      items.push({ kind: 'text', key: `text-${index}`, text: block.text })
      return
    }

    if (block.type !== 'tool_use') return

    if (isRenderTool(block.name)) {
      const artifact = artifacts?.[block.id]
      if (artifact) {
        items.push({ kind: 'artifact', key: block.id, artifact })
        return
      }
    }

    const call = buildToolCallSummary(
      block,
      toolCallStatuses,
      toolResults,
      supersededErrorIds.has(block.id),
    )
    appendToolCall(items, block, call)
  })

  // Any boundaries past the last block (e.g. step N+1 started but produced
  // no content yet): emit their predecessor markers so step N still gets a
  // chip even when step N+1 hasn't streamed anything.
  while (nextBoundaryIdx <= boundaries.length) {
    pushStepEnd(items, nextBoundaryIdx - 1)
    nextBoundaryIdx += 1
  }

  return items
}
